import { yupResolver } from '@hookform/resolvers/yup';
import { Delete } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, IconButton, InputLabel, DialogContent, Grid, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BsPencilSquare } from 'react-icons/bs';
import {
  ICommunicationServiceAccount,
  sharePhoneNumber,
  deleteCommunicationAccount,
  bulkUnAssign,
  usersAdvanceFilterQuery,
} from 'wisestep-api';
import {
  WsTypography,
  WsAutoComplete,
  WsButton,
  WsDialog,
  WsTypoVariants,
  WsDataGrid,
  WsDrawer,
  SearchBarNew,
  WsPageHeader,
} from 'wisestep-base';
import { array, object, string } from 'yup';

import { useWorkspaceContext } from '../../../context';
import {
  useSmsConfigAccessControl,
  SmsConfigAction,
} from '../../../hooks/useSmsConfigAccessControl';
import { success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { mapSmsAccounts, sharePhoneNumberPayload } from '../helper/payload';

import RechargeCommunicationCredits from './RechargeCommunicationCredits';
import SmsAccountSetting from './SmsAccountSetting';

const schema = object().shape({
  assignedRecruiters: array()
    .of(string().email('Please enter a valid email'))
    .min(0)
    .max(5, 'You can only assign 5 recruiters'),
});

export interface ICommunicationAccount {
  id: string | number;
  accountId: string;
  phoneNumber?: string;
  users?: number;
  sharedWith?: string[];
  action?: string;
}

export interface ICommunicationAccountsProps {
  communicationAccounts: ICommunicationServiceAccount[];
  credit?: number;
  deleteable?: boolean;
  refresh: () => Promise<void>;
  refreshLocalStorage: () => Promise<void>;
  getAccounts: () => Promise<void>;
}

const CommunicationAccounts = ({
  communicationAccounts,
  credit,
  deleteable = false,
  refresh,
  refreshLocalStorage,
  getAccounts,
}: ICommunicationAccountsProps) => {
  const admin = useWorkspaceContext();
  const [openAssignInput, setOpenAssignInput] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);
  const [openRechargeModal, setOpenRechargeModal] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string>('');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [initialAssignedRecruiters, setInitialAssignedRecruiters] = useState<string[]>([]);
  const [recruiterSuggestions, setRecruiterSuggestions] = useState<string[]>([]);
  const [smsData, setSmsData] = useState<ICommunicationAccount[]>(
    mapSmsAccounts(communicationAccounts)
  );
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowNo, setRowNo] = useState<number>(-1);

  const setNotification = useNotificationStore((state) => state.setNotification);
  const { access } = useSmsConfigAccessControl([
    SmsConfigAction.MANAGE_SMS_CONFIGURATION,
    SmsConfigAction.DELETE_SMS_CONFIGURATION,
    SmsConfigAction.MANAGE_SMS_USERS,
  ]);
  const [manageSmsConfig, deleteSmsConfig, manageSmsUsers] = access;

  const defaultValues = {
    assignedRecruiters: initialAssignedRecruiters,
    query: '',
  };

  const confirm = (value: boolean) => {
    setOpenModal(value);
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });

  const { getValues } = methods;

  const handleDrawer = (value: boolean, index: number) => {
    setSelectedPhoneNumber(communicationAccounts[0].phoneNumbers[index].phoneNumber);
    setSelectedAccountId(communicationAccounts[0].id);
    setInitialAssignedRecruiters(communicationAccounts[0].phoneNumbers[index].sharedWith ?? []);
    setSelectedOwner(communicationAccounts?.[0]?.phoneNumbers?.[index].owners?.[0] ?? '');
    setRowNo(index);
    setOpenDrawer(value);
  };

  const assignRecruiter = async () => {
    const selected = getValues('assignedRecruiters');
    const response = await sharePhoneNumber({
      ...sharePhoneNumberPayload,
      accountId: selectedAccountId,
      emails: selected,
      phoneNumber: selectedPhoneNumber,
    });
    setOpenModal(false);
    if (response.data.status === 'SHARED') {
      setNotification(success('Phone Number shared with recruiter successfully!'));
    } else {
      setNotification(failed(response.data.status));
    }
    refreshLocalStorage();
  };

  useEffect(() => {
    setSmsData(mapSmsAccounts(communicationAccounts));
    if (rowNo >= 0) {
      setSelectedPhoneNumber(communicationAccounts?.[0]?.phoneNumbers?.[rowNo]?.phoneNumber);
      setSelectedAccountId(communicationAccounts?.[0]?.id);
      setInitialAssignedRecruiters(communicationAccounts[0].phoneNumbers[rowNo].sharedWith ?? []);
      setSelectedOwner(communicationAccounts?.[0]?.phoneNumbers?.[rowNo]?.owners?.[0] ?? '');
    }
  }, [communicationAccounts]);

  const handleInput = async (input: string) => {
    const response = await usersAdvanceFilterQuery(input);
    setRecruiterSuggestions([]);
    response.data.map((recruiter) =>
      setRecruiterSuggestions((prev) => [...prev, recruiter?.userId])
    );
  };

  const clickBulkUnAssign = async () => {
    const selected = methods.getValues('assignedRecruiters');
    const response = await bulkUnAssign({
      emails: selected,
    });
    setOpenModal(false);
    if (response.data) {
      setNotification(success('Email has been unassigned successfully!'));
    } else {
      setNotification(failed('Error: while unassigning Email!'));
    }
    refreshLocalStorage();
  };

  const handleDeleteAccount = useCallback(
    async (index: number) => {
      await deleteCommunicationAccount(communicationAccounts[index].id ?? '');
      getAccounts();
    },
    [communicationAccounts, getAccounts]
  );

  const columns: GridColDef[] = [
    {
      headerName: 'ID',
      field: 'accountId',
      flex: 1.5,
      renderCell: (params) => (
        <Stack direction="row" spacing={2}>
          <WsTypography>{params.value}</WsTypography>{' '}
          {params.value && deleteable && (admin.owns || deleteSmsConfig) && (
            <IconButton onClick={() => handleDeleteAccount(params.row.index)}>
              <Delete />
            </IconButton>
          )}
        </Stack>
      ),
    },
    {
      headerName: 'Phone Numbers',
      field: 'phoneNumber',
      flex: 1,
    },
    {
      headerName: 'Users',
      field: 'users',
      flex: 0.5,
    },
    {
      headerName: 'Action',
      field: 'owners',
      flex: 0.5,
      renderCell: (params) => {
        return (
          <IconButton
            onClick={() => handleDrawer(true, params.row.id)}
            disableRipple
            disabled={!(admin.owns || manageSmsConfig || manageSmsUsers)}
          >
            <BsPencilSquare />
          </IconButton>
        );
      },
    },
  ];

  const unAssign = () => {
    setOpenAssignInput(false);
    setInitialAssignedRecruiters([]);
    setRecruiterSuggestions([]);
    setOpenModal(true);
  };

  const handleRecruiterTagsChange = (tags) => {
    methods.setValue('assignedRecruiters', tags);
    setInitialAssignedRecruiters(tags);
  };

  const handleSearch = () => {
    const search = getValues('query');
    const results = smsData.filter(
      (item) =>
        item?.phoneNumber?.includes(search) ||
        item?.sharedWith?.some((email) => email.toLowerCase()?.includes(search.toLowerCase()))
    );
    setSmsData(results);
  };
  const handleClear = () => {
    setSmsData(mapSmsAccounts(communicationAccounts));
  };

  return (
    <FormProvider {...methods}>
      {openRechargeModal && (
        <RechargeCommunicationCredits onClose={() => setOpenRechargeModal(false)} />
      )}

      <Grid item justifyContent="space-between" spacing={2}>
        {(admin.owns || manageSmsConfig || manageSmsUsers) && (
          <WsPageHeader
            title="SMS Accounts"
            buttonTitle="Bulk Un-Assign"
            onButtonClick={unAssign}
            rightChildren={
              <IconButton
                onClick={refresh}
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  height: 35,
                  width: 30,
                }}
              >
                <RefreshIcon />
              </IconButton>
            }
            {...(credit ? { text: `Credits earned: ${credit}` } : {})}
            transparent={true}
          />
        )}
      </Grid>
      <form onSubmit={methods.handleSubmit(assignRecruiter)}>
        <Grid item xs={5} sx={{ ml: 2 }}>
          <SearchBarNew
            size="small"
            name="query"
            placeholder="Search"
            type="submit"
            onClear={handleClear}
            onSearch={handleSearch}
          />
        </Grid>
        <Box>
          <WsDataGrid
            autoHeight
            rows={smsData}
            columns={columns}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
          {false && (admin.owns || manageSmsConfig || manageSmsUsers) && (
            <Stack spacing={1} py={2}>
              <InputLabel>{`Select Recruiters To Assign Phone Number: ${selectedPhoneNumber}`}</InputLabel>
              <WsAutoComplete
                name="assignedRecruiters"
                fullWidth
                size="small"
                multiple={true}
                initialValue={initialAssignedRecruiters}
                value={initialAssignedRecruiters}
                optionsInStringArray={recruiterSuggestions}
                onValueEnter={(input) => handleInput(input)}
                onTagsChange={(tags) => handleRecruiterTagsChange(tags)}
              />
              <div>
                <WsButton size="large" type="submit">
                  Save
                </WsButton>
              </div>
            </Stack>
          )}
        </Box>
      </form>
      <WsDialog size="medium" openDialog={openModal}>
        <DialogContent sx={{ p: 2 }}>
          {openAssignInput ? (
            <WsTypography variant={WsTypoVariants.TITLE}>
              Select Recruiters to Assign Phone Number : {selectedPhoneNumber}
            </WsTypography>
          ) : (
            <WsTypography variant={WsTypoVariants.TITLE}>
              Select Recruiters to Bulk Un-assign Phone Numbers
            </WsTypography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} mt={2}>
              <Stack direction="column">
                <WsTypography variant={WsTypoVariants.SUB_TITLE}>Email Address</WsTypography>
              </Stack>
              <WsAutoComplete
                name="assignedRecruiters"
                fullWidth
                size="small"
                multiple={true}
                value={initialAssignedRecruiters}
                initialValue={initialAssignedRecruiters}
                optionsInStringArray={recruiterSuggestions}
                onValueEnter={(input) => handleInput(input)}
                onTagsChange={(tags) => handleRecruiterTagsChange(tags)}
              />
            </Grid>
            <Grid item xs={12} spacing={2}>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <WsButton variant="outlined" size="large" onClick={() => confirm(false)}>
                  Cancel
                </WsButton>
                {openAssignInput ? (
                  <WsButton type="submit" size="large" onClick={() => assignRecruiter()}>
                    Assign
                  </WsButton>
                ) : (
                  <WsButton type="submit" size="large" onClick={() => clickBulkUnAssign()}>
                    Un Assign
                  </WsButton>
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
      </WsDialog>
      {openDrawer && (
        <WsDrawer
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          title="SMS Account Settings"
        >
          <SmsAccountSetting
            userId={selectedOwner}
            phoneNumber={selectedPhoneNumber}
            accountId={selectedAccountId}
            initialAssignedRecruiters={initialAssignedRecruiters}
            refreshLocalStorage={() => refreshLocalStorage()}
          />
        </WsDrawer>
      )}
    </FormProvider>
  );
};

export default CommunicationAccounts;
